import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

// Renders answer text with [label](url) as clickable links
function AnswerText({ text }) {
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const segments = [];
  let last = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) segments.push({ type: 'text', val: text.slice(last, m.index) });
    segments.push({ type: 'link', label: m[1], url: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) segments.push({ type: 'text', val: text.slice(last) });
  return (
    <>
      {segments.map((s, i) =>
        s.type === 'text' ? <span key={i}>{s.val}</span>
        : s.url.startsWith('/') ? (
          <Link key={i} to={s.url} style={{ color: '#14b8a6', textDecoration: 'underline' }}>{s.label}</Link>
        ) : (
          <a key={i} href={s.url} target="_blank" rel="noreferrer" style={{ color: '#14b8a6', textDecoration: 'underline' }}>{s.label}</a>
        )
      )}
    </>
  );
}

const CATEGORIES = [
  { key: 'using_app',   label: 'Using the App',             minRole: 'operator' },
  { key: 'quality',     label: 'Quality Issues',            minRole: 'operator' },
  { key: 'machine',     label: 'Machine Operation',         minRole: 'operator' },
  { key: 'safety',      label: 'Safety & Handling',         minRole: 'operator' },
  { key: 'parameters',  label: 'Parameter Troubleshooting', minRole: 'setter'   },
  { key: 'material',    label: 'Material Handling',         minRole: 'setter'   },
  { key: 'setup',       label: 'Mould & Machine Setup',     minRole: 'setter'   },
  { key: 'cost',        label: 'Cost & Reporting',          minRole: 'director' },
  { key: 'admin',       label: 'App Administration',        minRole: 'director' },
];

const ROLE_ORDER = { operator: 0, setter: 1, director: 2 };
const ROLE_LABEL = { operator: 'Operator', setter: 'Setter', director: 'Director' };
const ROLE_COLOR = { operator: '#3b82f6', setter: '#f59e0b', director: '#14b8a6' };

const S = {
  bg:     '#0f1117',
  card:   '#1e2329',
  border: '#2a3040',
  text:   '#f0f2f5',
  muted:  '#6b7280',
  teal:   '#14b8a6',
  amber:  '#f59e0b',
  red:    '#ef4444',
  green:  '#86efac',
  F:      'system-ui, -apple-system, sans-serif',
};

function printEntry(entry) {
  const categoryLabel = CATEGORIES.find(c => c.key === entry.category)?.label || entry.category;
  const links = (entry.related_links || []);
  const win = window.open('', '_blank');
  win.document.write(`<!DOCTYPE html><html><head>
    <title>${entry.title} — MouldOS</title>
    <style>
      body { font-family: system-ui, sans-serif; max-width: 700px; margin: 40px auto; color: #111; line-height: 1.6; }
      h1 { font-size: 22px; margin-bottom: 4px; }
      .meta { font-size: 12px; color: #666; margin-bottom: 24px; }
      .answer { white-space: pre-wrap; font-size: 14px; border-top: 1px solid #ddd; padding-top: 16px; }
      .links { margin-top: 24px; border-top: 1px solid #ddd; padding-top: 12px; font-size: 13px; }
      .footer { margin-top: 32px; font-size: 11px; color: #999; border-top: 1px solid #eee; padding-top: 8px; }
    </style>
  </head><body>
    <h1>${entry.title}</h1>
    <div class="meta">${categoryLabel} · Visible to: ${ROLE_LABEL[entry.min_role]}+</div>
    <div class="answer">${entry.answer.replace(/</g, '&lt;')}</div>
    ${links.length > 0 ? `<div class="links"><strong>Related:</strong><ul>${links.map(l => `<li>${l.label || l.url}</li>`).join('')}</ul></div>` : ''}
    <div class="footer">MouldOS · Printed ${new Date().toLocaleDateString()}</div>
  </body></html>`);
  win.document.close();
  win.focus();
  win.print();
}

// ── Entry editor modal ────────────────────────────────────────
function EntryModal({ entry, onSave, onClose }) {
  const isNew = !entry?.id;
  const [form, setForm] = useState({
    title:         entry?.title         || '',
    category:      entry?.category      || 'using_app',
    min_role:      entry?.min_role      || 'operator',
    answer:        entry?.answer        || '',
    related_links: entry?.related_links || [],
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');
  const [newLink, setNewLink] = useState({ label: '', url: '' });

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  async function save() {
    if (!form.title.trim() || !form.answer.trim()) { setErr('Title and answer are required.'); return; }
    setSaving(true); setErr('');
    try {
      if (isNew) await api.post('/api/v1/faq', form);
      else       await api.put(`/api/v1/faq/${entry.id}`, form);
      onSave();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
      setSaving(false);
    }
  }

  function addLink() {
    if (!newLink.url.trim()) return;
    set('related_links', [...form.related_links, { label: newLink.label || newLink.url, url: newLink.url }]);
    setNewLink({ label: '', url: '' });
  }

  function removeLink(i) { set('related_links', form.related_links.filter((_, idx) => idx !== i)); }

  const inp = { background: '#0f1117', border: `1px solid ${S.border}`, borderRadius: '6px', padding: '8px 10px', color: S.text, fontSize: '13px', fontFamily: S.F, width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ background: S.card, border: `1px solid ${S.border}`, borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontSize: '16px', fontWeight: '700', color: S.text, marginBottom: '20px' }}>
          {isNew ? 'New FAQ Entry' : 'Edit Entry'}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={lbl}>Title</label>
            <input style={inp} value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Flash on parting line — causes and fixes" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={lbl}>Category</label>
              <select style={inp} value={form.category} onChange={e => set('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Visible to</label>
              <select style={inp} value={form.min_role} onChange={e => set('min_role', e.target.value)}>
                <option value="operator">Everyone (Operator+)</option>
                <option value="setter">Setter &amp; Director</option>
                <option value="director">Director only</option>
              </select>
            </div>
          </div>

          <div>
            <label style={lbl}>Answer</label>
            <textarea style={{ ...inp, minHeight: '180px', resize: 'vertical', lineHeight: '1.6' }}
              value={form.answer} onChange={e => set('answer', e.target.value)}
              placeholder="Write the full answer here. Use plain text — line breaks are preserved." />
          </div>

          <div>
            <label style={lbl}>Related links</label>
            {form.related_links.map((l, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <div style={{ flex: 1, fontSize: '12px', color: S.teal, background: S.bg, border: `1px solid ${S.border}`, borderRadius: '5px', padding: '5px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {l.label || l.url}
                </div>
                <button onClick={() => removeLink(i)} style={{ background: 'none', border: 'none', color: S.red, cursor: 'pointer', fontSize: '14px', padding: '2px 6px' }}>×</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
              <input style={{ ...inp, flex: 1 }} placeholder="Label (optional)" value={newLink.label} onChange={e => setNewLink(l => ({ ...l, label: e.target.value }))} />
              <input style={{ ...inp, flex: 2 }} placeholder="URL or app path (e.g. /process)" value={newLink.url} onChange={e => setNewLink(l => ({ ...l, url: e.target.value }))} />
              <button onClick={addLink} style={{ background: S.teal, border: 'none', color: '#000', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>Add</button>
            </div>
          </div>
        </div>

        {err && <div style={{ marginTop: '14px', color: S.red, fontSize: '12px' }}>{err}</div>}

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: 'none', border: `1px solid ${S.border}`, color: S.muted, borderRadius: '7px', padding: '8px 18px', fontSize: '13px', cursor: 'pointer', fontFamily: S.F }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ background: S.teal, border: 'none', color: '#000', borderRadius: '7px', padding: '8px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: S.F }}>
            {saving ? 'Saving…' : isNew ? 'Create' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

const lbl = { display: 'block', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: S.muted, marginBottom: '6px' };

// ── Entry detail ──────────────────────────────────────────────
function EntryDetail({ id, onBack, onEdit, canEdit, canComment, userName, userRole }) {
  const { confirm } = useToast();
  const [entry, setEntry]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [comment, setComment]   = useState('');
  const [submitting, setSubmit] = useState(false);
  const [comErr, setComErr]     = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/v1/faq/${id}`)
      .then(r => { setEntry(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function submitComment() {
    if (!comment.trim()) return;
    setSubmit(true); setComErr('');
    try {
      await api.post(`/api/v1/faq/${id}/comments`, { body: comment });
      setComment('');
      load();
    } catch (e) {
      setComErr(e.response?.data?.error || e.message);
    } finally { setSubmit(false); }
  }

  async function approveComment(cid) {
    await api.put(`/api/v1/faq/${id}/comments/${cid}/approve`);
    load();
  }

  function deleteComment(cid) {
    confirm({
      message: 'Delete this comment?',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => { await api.delete(`/api/v1/faq/${id}/comments/${cid}`); load(); },
    });
  }

  if (loading) return <div style={{ padding: '40px', color: S.muted, fontFamily: S.F }}>Loading…</div>;
  if (!entry)  return <div style={{ padding: '40px', color: S.red,   fontFamily: S.F }}>Entry not found.</div>;

  const categoryLabel = CATEGORIES.find(c => c.key === entry.category)?.label || entry.category;
  const approvedComments   = entry.comments.filter(c => c.approved);
  const unapprovedComments = entry.comments.filter(c => !c.approved);

  return (
    <div style={{ fontFamily: S.F }}>
      {/* Back + actions bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'none', border: `1px solid ${S.border}`, color: S.muted, borderRadius: '6px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', fontFamily: S.F }}>
          ← Back
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={() => printEntry(entry)} style={{ background: 'none', border: `1px solid ${S.border}`, color: S.muted, borderRadius: '6px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', fontFamily: S.F }}>
          <i className="fa-solid fa-print" style={{ marginRight: '6px' }} />Print this page
        </button>
        {canEdit && (
          <button onClick={onEdit} style={{ background: 'none', border: `1px solid ${S.amber}`, color: S.amber, borderRadius: '6px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', fontFamily: S.F }}>
            Edit
          </button>
        )}
      </div>

      {/* Entry content */}
      <div style={{ background: S.card, border: `1px solid ${S.border}`, borderRadius: '10px', padding: '24px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '10px', fontWeight: '700', background: 'rgba(255,255,255,0.06)', color: S.muted, border: `1px solid ${S.border}`, borderRadius: '4px', padding: '2px 8px' }}>{categoryLabel}</span>
          <span style={{ fontSize: '10px', fontWeight: '700', background: `${ROLE_COLOR[entry.min_role]}22`, color: ROLE_COLOR[entry.min_role], border: `1px solid ${ROLE_COLOR[entry.min_role]}44`, borderRadius: '4px', padding: '2px 8px' }}>
            {ROLE_LABEL[entry.min_role]}+
          </span>
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: '700', color: S.text, margin: '0 0 20px 0', lineHeight: '1.3' }}>{entry.title}</h2>
        <div style={{ whiteSpace: 'pre-line', fontSize: '14px', color: '#d1d5db', lineHeight: '1.75' }}><AnswerText text={entry.answer} /></div>

        {(entry.related_links || []).length > 0 && (
          <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${S.border}` }}>
            <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: S.muted, marginBottom: '8px' }}>Related</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {entry.related_links.map((l, i) => (
                <a key={i} href={l.url} style={{ fontSize: '12px', color: S.teal, background: 'rgba(20,184,166,0.08)', border: `1px solid rgba(20,184,166,0.25)`, borderRadius: '5px', padding: '4px 10px', textDecoration: 'none' }}>
                  {l.label || l.url}
                </a>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: '16px', fontSize: '10px', color: S.muted }}>
          {entry.created_by && `Added by ${entry.created_by} · `}
          Updated {new Date(entry.updated_at).toLocaleDateString()}
        </div>
      </div>

      {/* Comments */}
      <div style={{ background: S.card, border: `1px solid ${S.border}`, borderRadius: '10px', padding: '20px' }}>
        <div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: S.muted, marginBottom: '16px' }}>
          Comments from the floor {approvedComments.length > 0 && `(${approvedComments.length})`}
        </div>

        {approvedComments.length === 0 && unapprovedComments.length === 0 && (
          <div style={{ fontSize: '13px', color: S.muted, marginBottom: '16px' }}>No comments yet.</div>
        )}

        {approvedComments.map(c => (
          <div key={c.id} style={{ borderBottom: `1px solid ${S.border}`, paddingBottom: '12px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: S.text }}>{c.author_name}</span>
                <span style={{ fontSize: '10px', color: ROLE_COLOR[c.author_role] || S.muted, background: `${ROLE_COLOR[c.author_role] || S.muted}22`, border: `1px solid ${ROLE_COLOR[c.author_role] || S.muted}44`, borderRadius: '3px', padding: '1px 6px' }}>
                  {ROLE_LABEL[c.author_role] || c.author_role}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '10px', color: S.muted }}>{new Date(c.created_at).toLocaleDateString()}</span>
                {canEdit && (
                  <button onClick={() => deleteComment(c.id)} style={{ background: 'none', border: 'none', color: S.red, cursor: 'pointer', fontSize: '12px', padding: '0 4px' }}>×</button>
                )}
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#d1d5db', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{c.body}</div>
          </div>
        ))}

        {/* Unapproved — director only */}
        {canEdit && unapprovedComments.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: S.amber, marginBottom: '10px' }}>
              Pending approval ({unapprovedComments.length})
            </div>
            {unapprovedComments.map(c => (
              <div key={c.id} style={{ background: 'rgba(245,158,11,0.06)', border: `1px solid rgba(245,158,11,0.2)`, borderRadius: '7px', padding: '10px 12px', marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <div>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: S.text }}>{c.author_name}</span>
                    <span style={{ fontSize: '10px', color: S.muted, marginLeft: '8px' }}>{new Date(c.created_at).toLocaleDateString()}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => approveComment(c.id)} style={{ background: 'rgba(134,239,172,0.1)', border: `1px solid rgba(134,239,172,0.3)`, color: S.green, borderRadius: '5px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer', fontFamily: S.F }}>Approve</button>
                    <button onClick={() => deleteComment(c.id)}  style={{ background: 'none', border: `1px solid rgba(239,68,68,0.3)`, color: S.red, borderRadius: '5px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer', fontFamily: S.F }}>Delete</button>
                  </div>
                </div>
                <div style={{ fontSize: '13px', color: '#d1d5db', whiteSpace: 'pre-wrap' }}>{c.body}</div>
              </div>
            ))}
          </div>
        )}

        {/* Add comment form (setter+) */}
        {canComment && (
          <div style={{ marginTop: '8px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: S.muted, marginBottom: '8px' }}>
              Add a note from experience
            </div>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Share what you've seen on the floor, what worked, what to watch out for…"
              style={{ width: '100%', boxSizing: 'border-box', background: S.bg, border: `1px solid ${S.border}`, borderRadius: '6px', padding: '10px', color: S.text, fontSize: '13px', fontFamily: S.F, minHeight: '80px', resize: 'vertical' }}
            />
            {comErr && <div style={{ color: S.red, fontSize: '12px', marginTop: '4px' }}>{comErr}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button onClick={submitComment} disabled={submitting || !comment.trim()} style={{ background: S.teal, border: 'none', color: '#000', borderRadius: '6px', padding: '7px 16px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: S.F, opacity: submitting || !comment.trim() ? 0.5 : 1 }}>
                {submitting ? 'Submitting…' : 'Submit comment'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main FAQ page ─────────────────────────────────────────────
export default function FAQ() {
  const { user, isDirector, can } = useAuth();
  const { confirm } = useToast();
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search,  setSearch]    = useState('');
  const [catFilter, setCat]     = useState('all');
  const [activeId, setActiveId] = useState(null);
  const [editEntry, setEditEntry] = useState(null); // null=closed, {}=new, entry=edit
  const [deleting, setDeleting] = useState(null);

  const canEdit    = isDirector();
  const canComment = can('comment_faq');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/v1/faq')
      .then(r => { setEntries(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function deleteEntry(id) {
    confirm({
      message: 'Delete this FAQ entry? This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setDeleting(id);
        await api.delete(`/api/v1/faq/${id}`);
        setDeleting(null);
        setActiveId(null);
        load();
      },
    });
  }
  // Permission-level hierarchy for available categories
  const permLevel = ROLE_ORDER[user?.permissionLevel] ?? 0;
  const availableCats = CATEGORIES.filter(c => ROLE_ORDER[c.minRole] <= permLevel);

  const filtered = entries.filter(e => {
    const matchCat = catFilter === 'all' || e.category === catFilter;
    const matchQ   = !search || e.title.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchQ;
  });

  // Group by category for the list view
  const grouped = availableCats.reduce((acc, cat) => {
    const items = filtered.filter(e => e.category === cat.key);
    if (items.length > 0 || catFilter === cat.key) acc.push({ cat, items });
    return acc;
  }, []);

  if (activeId) {
    const entry = entries.find(e => e.id === activeId);
    return (
      <div style={{ padding: '20px', background: S.bg, minHeight: '100vh', maxWidth: '760px', margin: '0 auto' }}>
        {editEntry && (
          <EntryModal entry={editEntry} onClose={() => setEditEntry(null)} onSave={() => { setEditEntry(null); load(); }} />
        )}
        <EntryDetail
          id={activeId}
          onBack={() => setActiveId(null)}
          onEdit={() => setEditEntry(entry)}
          canEdit={canEdit}
          canComment={canComment}
          userName={user?.name || user?.email}
          userRole={user?.role}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', background: S.bg, minHeight: '100vh', fontFamily: S.F, color: S.text }}>
      {editEntry !== null && (
        <EntryModal entry={editEntry} onClose={() => setEditEntry(null)} onSave={() => { setEditEntry(null); load(); }} />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', margin: '0 0 4px 0' }}>Knowledge Base</h1>
          <p style={{ fontSize: '13px', color: S.muted, margin: 0 }}>Common problems, troubleshooting guides, and process tips</p>
        </div>
        {canEdit && (
          <button onClick={() => setEditEntry({})} style={{ background: S.teal, border: 'none', color: '#000', borderRadius: '7px', padding: '9px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: S.F }}>
            + New Entry
          </button>
        )}
      </div>

      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search knowledge base…"
          style={{ width: '100%', boxSizing: 'border-box', background: S.card, border: `1px solid ${S.border}`, borderRadius: '8px', padding: '10px 14px', color: S.text, fontSize: '13px', fontFamily: S.F }}
        />
      </div>

      {/* Category tabs */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <CatBtn label="All" active={catFilter === 'all'} onClick={() => setCat('all')} />
        {availableCats.map(c => (
          <CatBtn key={c.key} label={c.label} active={catFilter === c.key} onClick={() => setCat(catFilter === c.key ? 'all' : c.key)} />
        ))}
      </div>

      {/* Entry list */}
      {loading ? (
        <div style={{ color: S.muted, padding: '40px', textAlign: 'center' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ color: S.muted, padding: '40px', textAlign: 'center', fontSize: '14px' }}>
          {entries.length === 0 ? 'No entries yet. Directors can add entries with the + New Entry button.' : 'No results match your search.'}
        </div>
      ) : (
        <div style={{ columns: '2 520px', columnGap: '20px' }}>
          {grouped.map(({ cat, items }) => (
            <div key={cat.key} style={{ breakInside: 'avoid', marginBottom: '24px' }}>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', color: S.teal, marginBottom: '8px', paddingBottom: '6px', borderBottom: `1px solid ${S.border}` }}>
                {cat.label}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {items.map(e => (
                  <EntryRow key={e.id} entry={e} onOpen={() => setActiveId(e.id)} onEdit={canEdit ? () => { setEditEntry(e); } : null} onDelete={canEdit ? () => deleteEntry(e.id) : null} deleting={deleting === e.id} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CatBtn({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{ background: active ? 'rgba(20,184,166,0.12)' : 'none', border: `1px solid ${active ? S.teal : S.border}`, color: active ? S.teal : S.muted, borderRadius: '6px', padding: '5px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: S.F }}>
      {label}
    </button>
  );
}

function EntryRow({ entry, onOpen, onEdit, onDelete, deleting }) {
  const categoryLabel = CATEGORIES.find(c => c.key === entry.category)?.label || entry.category;
  return (
    <div style={{ display: 'flex', alignItems: 'center', background: S.card, border: `1px solid ${S.border}`, borderRadius: '8px', padding: '12px 14px', cursor: 'pointer', gap: '12px' }}
      onClick={onOpen}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: S.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.title}</div>
        <div style={{ fontSize: '10px', color: S.muted, marginTop: '2px' }}>
          {categoryLabel} · Updated {new Date(entry.updated_at).toLocaleDateString()}
        </div>
      </div>
      <span style={{ fontSize: '10px', fontWeight: '700', color: ROLE_COLOR[entry.min_role], background: `${ROLE_COLOR[entry.min_role]}18`, border: `1px solid ${ROLE_COLOR[entry.min_role]}33`, borderRadius: '4px', padding: '2px 7px', flexShrink: 0 }}>
        {ROLE_LABEL[entry.min_role]}+
      </span>
      {onEdit && (
        <button onClick={e => { e.stopPropagation(); onEdit(); }} style={{ background: 'none', border: `1px solid ${S.border}`, color: S.muted, borderRadius: '5px', padding: '3px 9px', fontSize: '11px', cursor: 'pointer', fontFamily: S.F, flexShrink: 0 }}>Edit</button>
      )}
      {onDelete && (
        <button onClick={e => { e.stopPropagation(); onDelete(); }} disabled={deleting} style={{ background: 'none', border: `1px solid rgba(239,68,68,0.3)`, color: S.red, borderRadius: '5px', padding: '3px 9px', fontSize: '11px', cursor: 'pointer', fontFamily: S.F, flexShrink: 0, opacity: deleting ? 0.5 : 1 }}>
          {deleting ? '…' : 'Delete'}
        </button>
      )}
      <span style={{ color: S.muted, fontSize: '14px', flexShrink: 0 }}>›</span>
    </div>
  );
}
